import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Link2, ListChecks, Pin, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errText } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { inkOn, paperStyle } from "@/lib/postItStyles";
import { longDate, shortDate } from "@/lib/eventMeta";

const exact = { printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" };

export default function BoardSnapshot() {
  const [params] = useSearchParams();
  const filter = params.get("filter") || "all";
  const needle = (params.get("q") || "").trim().toLowerCase();
  const [notes, setNotes] = useState(null);
  const [includeExpired, setIncludeExpired] = useState(params.get("expired") === "1");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/postits");
      setNotes(data);
    } catch (e) {
      toast.error(errText(e));
      setNotes([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    return (notes || []).filter((n) => {
      if (filter === "high" && n.priority !== "high") return false;
      if (filter === "pinned" && !n.pinned) return false;
      if (filter === "linked" && !(n.matter_id || n.event_id || n.todo_id)) return false;
      if (filter === "expired") return n.expired;
      if (n.expired && !includeExpired) return false;
      if (!needle) return true;
      return [n.title, n.body, n.matter_title, n.event_label, n.todo_title]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle));
    });
  }, [notes, filter, needle, includeExpired]);

  useEffect(() => {
    if (notes) {
      const id = setTimeout(() => window.print(), 700);
      return () => clearTimeout(id);
    }
  }, [notes]);

  const count = shown.length;
  const cols = count <= 4 ? 2 : count <= 9 ? 3 : count <= 20 ? 4 : 5;
  const dense = count > 12;
  const clamp = count <= 6 ? 14 : count <= 12 ? 8 : count <= 20 ? 5 : 3;

  return (
    <div className="min-h-screen bg-white text-black" data-testid="board-snapshot">
      <style>{`@media print { @page { size: A4 portrait; margin: 8mm; } }`}</style>
      <div className="mx-auto max-w-[900px] px-6 py-8">
        <div className="flex items-start justify-between gap-6 border-b-2 border-black pb-3">
          <div>
            <h1 className="font-heading text-2xl font-bold">Post-It board</h1>
            <p className="mt-1 text-xs text-neutral-600" data-testid="snapshot-meta">
              Chambers of Amir Arsiwala · {longDate(new Date())} · {count} note
              {count === 1 ? "" : "s"}
              {filter !== "all" ? ` · ${filter}` : ""}
              {needle ? ` · “${params.get("q")}”` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <Button
              variant="outline"
              data-testid="snapshot-expired-toggle"
              onClick={() => setIncludeExpired((v) => !v)}
              className="rounded-full h-10 px-4 text-xs"
            >
              {includeExpired ? "Hide expired" : "Include expired"}
            </Button>
            <Button
              data-testid="snapshot-print-btn"
              onClick={() => window.print()}
              className="rounded-full h-10 px-4"
            >
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        {notes === null ? <p className="mt-8 text-sm">Preparing the board…</p> : null}

        {notes !== null && count === 0 ? (
          <p className="mt-8 text-sm text-neutral-600" data-testid="snapshot-empty">
            Nothing on the board to print.
          </p>
        ) : null}

        {count ? (
          <div
            className="mt-4 grid gap-2.5"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            data-testid="snapshot-grid"
          >
            {shown.map((note) => {
              const paper = paperStyle(note.color, note.texture);
              const light = inkOn(note.color) === "light";
              return (
                <article
                  key={note.post_it_id}
                  data-testid={`snapshot-note-${note.post_it_id}`}
                  className="break-inside-avoid rounded-sm border border-neutral-400/60 p-2.5"
                  style={{ ...paper, ...exact }}
                >
                  <div className="flex items-center gap-1.5 text-[0.55rem] font-bold uppercase tracking-widest">
                    {note.pinned ? <Pin className="h-2.5 w-2.5" /> : null}
                    {note.priority === "high" ? <span>High</span> : null}
                    {note.expired ? <span>Expired</span> : null}
                  </div>
                  {note.title ? (
                    <h2
                      className={`font-heading font-bold leading-tight ${dense ? "text-[0.72rem]" : "text-sm"}`}
                    >
                      {note.title}
                    </h2>
                  ) : null}
                  <div
                    className={`mt-1 leading-snug ${dense ? "text-[0.62rem]" : "text-[0.72rem]"}`}
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: clamp,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    <Markdown text={note.body} />
                  </div>
                  {note.expires_on ? (
                    <p className="mt-1.5 inline-flex items-center gap-1 text-[0.55rem] font-semibold uppercase tracking-wider">
                      <CalendarClock className="h-2.5 w-2.5" /> {shortDate(note.expires_on)}
                    </p>
                  ) : null}
                  {note.matter_title || note.event_label || note.todo_title ? (
                    <div
                      className="mt-1.5 space-y-0.5 border-t pt-1 text-[0.58rem]"
                      style={{ borderColor: light ? "rgba(255,255,255,0.3)" : "rgba(23,23,23,0.18)" }}
                    >
                      {note.matter_title ? (
                        <p className="flex items-center gap-1 truncate">
                          <Link2 className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">{note.matter_title}</span>
                        </p>
                      ) : null}
                      {note.event_label ? (
                        <p className="flex items-center gap-1 truncate">
                          <CalendarClock className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">{note.event_label}</span>
                        </p>
                      ) : null}
                      {note.todo_title ? (
                        <p className="flex items-center gap-1 truncate">
                          <ListChecks className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">{note.todo_title}</span>
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}

        <p className="mt-6 text-[0.6rem] text-neutral-500 border-t border-neutral-300 pt-2">
          One-page snapshot of the board — long notes are trimmed to fit.
        </p>
      </div>
    </div>
  );
}
